import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, DatePicker, Select, Space, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useApiGateway from '../../../apiGateway';
import {
  RESPONSE_TIME_METRICS,
  type ResponseTimeEvent,
  type ResponseTimeHistoryRow,
  type ResponseTimeMetric,
} from '../../../types/responseTimeStatistics';

const EVENT_OPTIONS: Array<{ value: ResponseTimeEvent; label: string }> = [
  { value: 'sspBidResponse', label: 'SSP bid response (bid)' },
  { value: 'dspBidResponseBid', label: 'DSP bid response (bid)' },
  { value: 'dspBidResponseNoBid', label: 'DSP bid response (no-bid)' },
];

const METRIC_OPTIONS = RESPONSE_TIME_METRICS.map((m) => ({ value: m, label: m }));

const RANGE_PRESETS: Array<{ label: string; value: [Dayjs, Dayjs] }> = [
  { label: 'Last hour', value: [dayjs().subtract(1, 'hour'), dayjs()] },
  { label: 'Last 6h', value: [dayjs().subtract(6, 'hour'), dayjs()] },
  { label: 'Last 24h', value: [dayjs().subtract(24, 'hour'), dayjs()] },
  { label: 'Last 7d', value: [dayjs().subtract(7, 'day'), dayjs()] },
  { label: 'Last 30d', value: [dayjs().subtract(30, 'day'), dayjs()] },
];

const SERIES_COLORS = [
  '#1677ff',
  '#52c41a',
  '#fa8c16',
  '#eb2f96',
  '#13c2c2',
  '#722ed1',
  '#f5222d',
  '#a0d911',
  '#fadb14',
  '#2f54eb',
  '#fa541c',
  '#08979c',
  '#531dab',
  '#c41d7f',
  '#7cb305',
  '#096dd9',
  '#d46b08',
  '#389e0d',
  '#cf1322',
  '#10239e',
];

type PartnerOption = { value: number; label: string };

const toUtcChKey = (ms: number): string => new Date(ms).toISOString().slice(0, 19).replace('T', ' ');

export function ResponseTimePage() {
  const apiGateway = useApiGateway();

  const [event, setEvent] = useState<ResponseTimeEvent>('sspBidResponse');
  const [partnerIds, setPartnerIds] = useState<number[]>([]);
  const [metric, setMetric] = useState<ResponseTimeMetric>('p50');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(6, 'hour'), dayjs()]);

  const [ssps, setSsps] = useState<PartnerOption[]>([]);
  const [dsps, setDsps] = useState<PartnerOption[]>([]);
  const [rows, setRows] = useState<ResponseTimeHistoryRow[]>([]);
  const [bucketMinutes, setBucketMinutes] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [sspsResponse, dspsResponse] = await Promise.all([
          apiGateway.ssps.getAll({ includeSpend: false }),
          apiGateway.dsps.getAll({ includeSpend: false }),
        ]);

        const toOptions = (list: Array<{ id: number; name?: string }>): PartnerOption[] =>
          _.sortBy(
            (list || []).map((p) => ({ value: p.id, label: `[${p.id}] ${p.name || ''}`.trim() })),
            'value'
          );

        setSsps(toOptions(sspsResponse));
        setDsps(toOptions(dspsResponse));
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, []);

  const partnerOptions = event === 'sspBidResponse' ? ssps : dsps;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGateway.responseTimeStatistics.getHistory({
        event,
        from: range[0].toISOString(),
        to: range[1].toISOString(),
        partnerId: partnerIds.length ? partnerIds : undefined,
      });

      setRows(response.rows);
      setBucketMinutes(response.bucketMinutes);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [event, partnerIds, range]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const seriesNames = useMemo(() => _.uniq(rows.map((row) => row.partnerLabel)), [rows]);

  // Full bucket axis so ingestion gaps render as holes, not interpolation.
  const chartData = useMemo(() => {
    const bucketMs = bucketMinutes * 60_000;
    const startMs = Math.floor(range[0].valueOf() / bucketMs) * bucketMs;
    const endMs = range[1].valueOf();

    const byTime = new Map<string, Record<string, string | number | null>>();

    for (let ms = startMs; ms <= endMs; ms += bucketMs) {
      byTime.set(toUtcChKey(ms), { t: toUtcChKey(ms) });
    }

    rows.forEach((row) => {
      const point = byTime.get(row.t);
      if (point) {
        point[row.partnerLabel] = row[metric];
        point[`${row.partnerLabel}__total`] = row.totalResponses;
      }
    });

    return Array.from(byTime.values());
  }, [rows, metric, bucketMinutes, range]);

  return (
    <div className="xchange-page">
      <Card title="Response Time History" size="small" variant="outlined" style={{ marginTop: 16 }}>
        <Space wrap className="xchange-filters" style={{ marginBottom: 16 }}>
          <Select
            options={EVENT_OPTIONS}
            value={event}
            onChange={(value) => {
              setEvent(value);
              setPartnerIds([]);
            }}
            style={{ width: 220 }}
          />
          <Select
            mode="multiple"
            options={partnerOptions}
            value={partnerIds}
            onChange={setPartnerIds}
            placeholder={event === 'sspBidResponse' ? 'All SSPs' : 'All DSPs'}
            // Fixed width: with maxTagCount="responsive" a content-sized
            // select oscillates (tag collapse changes width changes collapse),
            // reflowing the chart every frame.
            style={{ width: 320 }}
            allowClear
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
          />
          <Select options={METRIC_OPTIONS} value={metric} onChange={setMetric} style={{ width: 140 }} />
          <DatePicker.RangePicker
            showTime
            value={range}
            presets={RANGE_PRESETS}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
            }}
          />
        </Space>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

        <ResponsiveContainer width="100%" height={480}>
          <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="t" tickFormatter={(t: string) => t.slice(5, 16)} minTickGap={48} />
            <YAxis
              label={{
                value: metric === 'totalResponses' ? 'responses' : 'ms (UTC buckets)',
                angle: -90,
                position: 'insideLeft',
              }}
            />
            <Tooltip
              formatter={(
                value: number | string,
                name: string,
                item: { payload?: Record<string, number | string | null> }
              ) => {
                if (metric === 'totalResponses') return [value, name];
                const total = item.payload?.[`${name}__total`];
                return [`${value} ms (${total ?? '–'} responses)`, name];
              }}
            />
            <Legend />
            {seriesNames.map((name, i) => (
              <Line
                key={name}
                dataKey={name}
                type="monotone"
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>

        <Typography.Text type="secondary">
          {loading
            ? 'Loading…'
            : `Bucket: ${bucketMinutes} min · Times in UTC · Percentiles merged across nodes are totalResponses-weighted approximations`}
        </Typography.Text>
      </Card>
    </div>
  );
}
